import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Assignment,
  AssignmentSubmission,
  Attendance,
  Class,
  LEAVE_REQUEST_STATUS,
  LeaveRequest,
  Quiz,
  QuizAttempt,
  Student,
  Teacher,
} from "../../models";
import { AuthenticatedRequest } from "../../types/auth-types";
import { APIResponse } from "../../types/response-types";
import { logger } from "../../utils/logger";

/**
 * Student Mobile Controller - Handles mobile-specific student operations
 * Separate from school flows to avoid conflicts
 */
export class StudentMobileController {
  /**
   * Remove specific documents from student profile using media IDs (Mobile App)
   * This is separate from school administrative flows
   */
  async removeStudentDocuments(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only students can remove their own documents via mobile
      if (userRole !== "student") {
        res.status(403).json({
          success: false,
          message:
            "Only students can remove their own documents via mobile app",
        } as APIResponse);
        return;
      }

      const { removeDocuments } = req.body;

      if (
        !removeDocuments ||
        !Array.isArray(removeDocuments) ||
        removeDocuments.length === 0
      ) {
        res.status(400).json({
          success: false,
          message:
            "Please provide removeDocuments array with media IDs to remove",
        } as APIResponse);
        return;
      }

      // Find student profile by userId
      const studentProfile = await Student.findOne({ userId });
      if (!studentProfile) {
        res.status(404).json({
          success: false,
          message: "Student profile not found. Please create a profile first.",
        } as APIResponse);
        return;
      }

      if (!studentProfile.documents || studentProfile.documents.length === 0) {
        res.status(400).json({
          success: false,
          message: "No documents found to remove.",
        } as APIResponse);
        return;
      }

      // Remove documents by fileName (media ID)
      const originalCount = studentProfile.documents.length;
      const documentsToRemove: string[] = [];

      studentProfile.documents = studentProfile.documents.filter((doc: any) => {
        if (removeDocuments.includes(doc.fileName)) {
          documentsToRemove.push(doc.fileName);
          return false;
        }
        return true;
      });

      const removedCount = originalCount - studentProfile.documents.length;

      if (removedCount === 0) {
        res.status(404).json({
          success: false,
          message: "No matching documents found to remove.",
        } as APIResponse);
        return;
      }

      await studentProfile.save();

      logger.info(
        `Student mobile: ${removedCount} documents removed from student profile for user: ${authReq.user.email}`,
        {
          userId: authReq.user.userId,
          studentId: studentProfile.studentId,
          removedDocuments: documentsToRemove,
          source: "mobile",
        },
      );

      res.status(200).json({
        success: true,
        message: `${removedCount} document(s) removed successfully`,
        data: {
          removedCount,
          removedDocuments: documentsToRemove,
          remainingDocuments: studentProfile.documents.length,
          documents: studentProfile.documents,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student mobile - Remove documents error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update student profile with document changes (Mobile App)
   */
  async updateStudentProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const userRole = authReq.user.role;

      // Only students can update their own profile via mobile
      if (userRole !== "student") {
        res.status(403).json({
          success: false,
          message: "Only students can update their own profile via mobile app",
        } as APIResponse);
        return;
      }

      const { removeDocuments } = req.body;

      if (!removeDocuments || !Array.isArray(removeDocuments)) {
        res.status(400).json({
          success: false,
          message: "Please provide removeDocuments array with media IDs",
        } as APIResponse);
        return;
      }

      // Find student profile by userId
      const studentProfile = await Student.findOne({ userId });
      if (!studentProfile) {
        res.status(404).json({
          success: false,
          message: "Student profile not found. Please create a profile first.",
        } as APIResponse);
        return;
      }

      let removedCount = 0;
      const documentsToRemove: string[] = [];

      // Remove documents if requested
      if (removeDocuments.length > 0) {
        if (
          !studentProfile.documents ||
          studentProfile.documents.length === 0
        ) {
          res.status(400).json({
            success: false,
            message: "No documents found to remove.",
          } as APIResponse);
          return;
        }

        const originalCount = studentProfile.documents.length;

        studentProfile.documents = studentProfile.documents.filter(
          (doc: any) => {
            if (removeDocuments.includes(doc.fileName)) {
              documentsToRemove.push(doc.fileName);
              return false;
            }
            return true;
          },
        );

        removedCount = originalCount - studentProfile.documents.length;
      }

      await studentProfile.save();

      logger.info(
        `Student mobile profile update: ${removedCount} documents removed for user: ${authReq.user.email}`,
        {
          userId: authReq.user.userId,
          studentId: studentProfile.studentId,
          removedDocuments: documentsToRemove,
          source: "mobile",
        },
      );

      res.status(200).json({
        success: true,
        message: `Profile updated successfully. ${removedCount} document(s) removed.`,
        data: {
          removedCount,
          removedDocuments: documentsToRemove,
          remainingDocuments: studentProfile.documents?.length || 0,
          documents: studentProfile.documents || [],
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student mobile - Update profile error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get student's profile (Mobile App)
   * @route GET /api/v1/mobile/student/profile
   * @access Private (Student)
   */
  async getStudentProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;

      const student = await Student.findOne({ userId })
        .populate("schoolId", "schoolName schoolCode logo")
        .populate(
          "majorSubjectsIds",
          "className classCode courseName courseCode",
        )
        .populate("majorCourseIds", "courseCode courseName")
        .select("-documents"); // Exclude documents for mobile

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Add majorSubjects with id, code, name for display (prefer majorCourseIds)
      const coursePopulated =
        (student.majorCourseIds as any[])?.filter(Boolean) || [];
      const classPopulated =
        (student.majorSubjectsIds as any[])?.filter(Boolean) || [];
      const majorSubjects =
        coursePopulated.length > 0
          ? coursePopulated.map((c: any) => ({
              id: String(c._id),
              name: c.courseName || c.courseCode || "",
              code: c.courseCode || "",
            }))
          : classPopulated.map((cls: any) => ({
              id: String(cls._id),
              name: cls.courseName || cls.className || "",
              code: cls.courseCode || cls.classCode || "",
            }));

      const profileWithSubjects = student.toObject
        ? student.toObject()
        : Object.assign({}, student);
      (profileWithSubjects as any).majorSubjects = majorSubjects;

      res.status(200).json({
        success: true,
        message: "Profile retrieved successfully",
        data: {
          profile: profileWithSubjects,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student mobile - Get profile error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update student profile (Mobile App)
   * @route PUT /api/v1/mobile/student/profile
   * @access Private (Student)
   */
  async updateProfile(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const {
        fullName,
        phone,
        address,
        academicLevel,
        academicYear,
        majorSubjectsIds,
        majorCourseIds,
        minorSubjects,
      } = req.body;

      const student = await Student.findOne({ userId });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Update fields if provided
      if (fullName !== undefined) student.fullName = fullName;
      if (phone !== undefined) student.phone = phone;
      if (address !== undefined) student.address = address;
      if (academicLevel !== undefined) student.academicLevel = academicLevel;
      if (academicYear !== undefined) student.academicYear = academicYear;
      if (majorSubjectsIds !== undefined) {
        student.majorSubjectsIds = Array.isArray(majorSubjectsIds)
          ? majorSubjectsIds
              .filter((id: string) => mongoose.Types.ObjectId.isValid(id))
              .map((id: string) => new mongoose.Types.ObjectId(id))
          : [];
      }
      if (majorCourseIds !== undefined) {
        student.majorCourseIds = Array.isArray(majorCourseIds)
          ? majorCourseIds
              .filter((id: string) => mongoose.Types.ObjectId.isValid(id))
              .map((id: string) => new mongoose.Types.ObjectId(id))
          : [];
      }
      if (minorSubjects !== undefined) student.minorSubjects = minorSubjects;

      await student.save();

      logger.info(`Student mobile profile updated: ${student.id}`, {
        userId,
        studentId: student.studentId,
      });

      res.status(200).json({
        success: true,
        message: "Profile updated successfully",
        data: {
          profile: student,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student mobile - Update profile error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get student's assignments (Mobile App)
   * @route GET /api/v1/mobile/student/assignments
   * @access Private (Student)
   */
  async getAssignments(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId, status, page = 1, limit = 20 } = req.query;

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Get student's classes
      const studentClasses = await Class.find({
        schoolId: student.schoolId,
        studentIds: student._id,
      }).select("_id");

      const classIds = studentClasses.map(c => c._id);

      // Build query
      const filterQuery: any = {
        classId: { $in: classIds },
        status: "published",
      };

      if (classId) {
        filterQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const assignments = await Assignment.find(filterQuery)
        .populate("class", "className courseCode")
        .populate("semester", "semesterName academicYear")
        .sort({ deadline: 1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Assignment.countDocuments(filterQuery);

      // Get submission status for each assignment
      const assignmentsWithStatus = await Promise.all(
        assignments.map(async assignment => {
          const submission = await AssignmentSubmission.findOne({
            assignmentId: assignment._id,
            studentId: student._id,
          });

          return {
            id: assignment.id,
            title: assignment.title,
            description: assignment.description,
            totalMarks: assignment.totalMarks,
            deadline: assignment.deadline,
            allowLateSubmission: assignment.allowLateSubmission,
            class: (assignment as any).class,
            semester: (assignment as any).semester,
            materials: assignment.materials || [],
            submission: submission
              ? {
                  id: submission.id,
                  submittedAt: submission.submittedAt,
                  status: submission.status,
                  marksObtained: submission.marksObtained,
                  isLateSubmission: submission.isLateSubmission,
                }
              : null,
            createdAt: assignment.createdAt,
          };
        }),
      );

      res.status(200).json({
        success: true,
        message: "Assignments retrieved successfully",
        data: {
          assignments: assignmentsWithStatus,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student mobile - Get assignments error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get student's attendance (Mobile App)
   * @route GET /api/v1/mobile/student/attendance
   * @access Private (Student)
   */
  async getAttendance(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId, startDate, endDate } = req.query;

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        studentId: student._id,
      };

      if (classId) {
        filterQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      if (startDate || endDate) {
        filterQuery.date = {};
        if (startDate) {
          filterQuery.date.$gte = new Date(startDate as string);
        }
        if (endDate) {
          filterQuery.date.$lte = new Date(endDate as string);
        }
      }

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const attendance = await Attendance.find(filterQuery)
        .populate("class", "className courseCode")
        .sort({ date: -1 });

      // Get statistics
      const stats = await Attendance.getStatistics(
        classId ? new mongoose.Types.ObjectId(classId as string) : undefined,
        student._id,
        startDate ? new Date(startDate as string) : undefined,
        endDate ? new Date(endDate as string) : undefined,
      );

      res.status(200).json({
        success: true,
        message: "Attendance retrieved successfully",
        data: {
          attendance: attendance.map(a => {
            const aDoc = a as any;
            return {
              id: a.id,
              class: aDoc.class
                ? {
                    id: aDoc.class.id || aDoc.class._id,
                    className: aDoc.class.className,
                    courseCode: aDoc.class.courseCode,
                  }
                : null,
              status: a.status,
              remarks: a.remarks,
              date: a.date,
            };
          }),
          statistics: stats,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student mobile - Get attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get student's quizzes (Mobile App)
   * @route GET /api/v1/mobile/student/quizzes
   * @access Private (Student)
   */
  async getQuizzes(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { classId, status, page = 1, limit = 20 } = req.query;

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Get student's classes
      const studentClasses = await Class.find({
        schoolId: student.schoolId,
        studentIds: student._id,
      }).select("_id");

      const classIds = studentClasses.map(c => c._id);

      // Build query
      const filterQuery: any = {
        classId: { $in: classIds },
        status: "published",
      };

      if (classId) {
        filterQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const quizzes = await Quiz.find(filterQuery)
        .populate("class", "className courseCode")
        .populate("semester", "semesterName academicYear")
        .sort({ deadline: 1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Quiz.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Quizzes retrieved successfully",
        data: {
          quizzes: quizzes.map(quiz => ({
            id: quiz.id,
            title: quiz.title,
            description: quiz.description,
            totalMarks: quiz.totalMarks,
            duration: quiz.duration,
            deadline: quiz.deadline,
            startTime: quiz.startTime,
            class: (quiz as any).class,
            semester: (quiz as any).semester,
            createdAt: quiz.createdAt,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student mobile - Get quizzes error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get student's progress report (Mobile App)
   * Shows assignments, quizzes, and class performance
   * @route GET /api/v1/mobile/student/progress-report
   * @access Private - Student only
   */
  async getProgressReport(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;

      // Find student
      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      }).populate("schoolId", "schoolName");

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Get all classes student is enrolled in
      const studentClasses = await Class.find({
        studentIds: student._id,
        isActive: true,
      })
        .populate("semesterId", "semesterName academicYear")
        .populate("teacherIds", "fullName employeeId");

      const progressData = [];

      for (const classItem of studentClasses) {
        const classId = classItem._id;
        const semesterId = classItem.semesterId;

        // Get assignments for this class
        const assignments = await Assignment.find({
          classId,
          semesterId,
          status: "published",
        });

        const assignmentProgress = [];
        let totalAssignmentMarks = 0;
        let obtainedAssignmentMarks = 0;

        for (const assignment of assignments) {
          const submission = await AssignmentSubmission.findOne({
            assignmentId: assignment._id,
            studentId: student._id,
          });

          if (submission && submission.isGraded()) {
            const marksObtained = submission.marksObtained || 0;
            const percentage = Math.round(
              (marksObtained / assignment.totalMarks) * 100,
            );

            // Calculate grade letter
            let grade = "";
            if (percentage >= 97) grade = "A+";
            else if (percentage >= 93) grade = "A";
            else if (percentage >= 90) grade = "A-";
            else if (percentage >= 87) grade = "B+";
            else if (percentage >= 83) grade = "B";
            else if (percentage >= 80) grade = "B-";
            else if (percentage >= 77) grade = "C+";
            else if (percentage >= 73) grade = "C";
            else if (percentage >= 70) grade = "C-";
            else if (percentage >= 67) grade = "D+";
            else if (percentage >= 65) grade = "D";
            else grade = "F";

            assignmentProgress.push({
              id: assignment.id,
              title: assignment.title,
              isLateSubmission: submission.isLateSubmission,
              marksObtained,
              totalMarks: assignment.totalMarks,
              percentage,
              grade: submission.isLateSubmission
                ? `${grade} | ${percentage}%`
                : `${grade} | ${percentage}%`,
            });

            totalAssignmentMarks += assignment.totalMarks;
            obtainedAssignmentMarks += marksObtained;
          } else if (submission) {
            // Submitted but not graded
            assignmentProgress.push({
              id: assignment.id,
              title: assignment.title,
              isLateSubmission: submission.isLateSubmission,
              marksObtained: null,
              totalMarks: assignment.totalMarks,
              percentage: null,
              grade: "Pending",
            });
          }
        }

        // Get quizzes for this class
        const quizzes = await Quiz.find({
          classId,
          semesterId,
          status: "published",
        });

        const quizProgress = [];
        let totalQuizMarks = 0;
        let obtainedQuizMarks = 0;

        for (const quiz of quizzes) {
          const attempt = await QuizAttempt.findOne({
            quizId: quiz._id,
            studentId: student._id,
            status: "graded",
          }).sort({ submittedAt: -1 });

          if (attempt && attempt.totalMarksObtained !== undefined) {
            const marksObtained = attempt.totalMarksObtained;
            const percentage = Math.round(
              (marksObtained / quiz.totalMarks) * 100,
            );

            quizProgress.push({
              id: quiz.id,
              title: quiz.title,
              marksObtained,
              totalMarks: quiz.totalMarks,
              percentage,
              grade: `${percentage}%`,
            });

            totalQuizMarks += quiz.totalMarks;
            obtainedQuizMarks += marksObtained;
          } else if (attempt) {
            // Attempted but not graded
            quizProgress.push({
              id: quiz.id,
              title: quiz.title,
              marksObtained: null,
              totalMarks: quiz.totalMarks,
              percentage: null,
              grade: "Pending",
            });
          }
        }

        // Calculate overall class performance
        const totalMarks = totalAssignmentMarks + totalQuizMarks;
        const obtainedMarks = obtainedAssignmentMarks + obtainedQuizMarks;
        const classPerformancePercentage =
          totalMarks > 0 ? Math.round((obtainedMarks / totalMarks) * 100) : 0;

        // Get teacher info
        const teachers = (classItem.teacherIds as any[]) || [];
        const primaryTeacher =
          teachers.length > 0
            ? await Teacher.findById(teachers[0]).select("fullName employeeId")
            : null;

        progressData.push({
          class: {
            id: classItem.id,
            className: classItem.className,
            courseCode: classItem.courseCode || "",
            courseName: classItem.courseName || "",
          },
          instructor: primaryTeacher
            ? {
                id: primaryTeacher.id,
                fullName: primaryTeacher.fullName,
                employeeId: primaryTeacher.employeeId,
              }
            : null,
          assignments: assignmentProgress,
          quizzes: quizProgress,
          classPerformance: {
            percentage: classPerformancePercentage,
            grade: (() => {
              if (classPerformancePercentage >= 90) return "A";
              if (classPerformancePercentage >= 80) return "B";
              if (classPerformancePercentage >= 70) return "C";
              if (classPerformancePercentage >= 60) return "D";
              return "F";
            })(),
          },
        });
      }

      res.status(200).json({
        success: true,
        message: "Progress report retrieved successfully",
        data: {
          progress: progressData,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Student mobile - Get progress report error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Create absence/leave request with document upload (Mobile App)
   * @route POST /api/v1/mobile/student/absence-request
   * @access Private - Student only
   */
  async createAbsenceRequest(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthenticatedRequest;
      const userId = authReq.user.userId;
      const { leaveType, startDate, endDate, reason } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Validate required fields
      if (!leaveType || !startDate || !endDate || !reason) {
        res.status(400).json({
          success: false,
          message:
            "Missing required fields: leaveType, startDate, endDate, reason",
        } as APIResponse);
        return;
      }

      // Find student profile
      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      // Validate dates
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        res.status(400).json({
          success: false,
          message: "Invalid date format",
        } as APIResponse);
        return;
      }

      if (end < start) {
        res.status(400).json({
          success: false,
          message: "End date must be after or equal to start date",
        } as APIResponse);
        return;
      }

      // Handle file uploads (if any)
      const documents: any[] = [];
      if (req.files && Array.isArray(req.files)) {
        for (const file of req.files) {
          // Get base URL from environment
          const baseUrl =
            process.env.IMAGE_URL || "http://localhost:3080/uploads";
          const subDir = file.destination
            ? file.destination
                .replace(/^uploads[\/\\]/, "")
                .replaceAll("\\", "/")
            : "documents/";
          const fileUrl = `${baseUrl}/${subDir}${file.filename}`;

          documents.push({
            fileName: file.filename,
            originalName: file.originalname,
            fileSize: file.size,
            mimeType: file.mimetype,
            fileUrl,
            uploadedAt: new Date(),
          });
        }
      }

      // Create leave request
      const leaveRequest = new LeaveRequest({
        studentId: student._id,
        schoolId: student.schoolId,
        leaveType: leaveType || "other",
        startDate: start,
        endDate: end,
        reason,
        documents: documents.length > 0 ? documents : undefined,
        status: LEAVE_REQUEST_STATUS.PENDING,
      });

      await leaveRequest.save();

      // Populate student info for response
      const populatedRequest = await LeaveRequest.findById(
        leaveRequest._id,
      ).populate("student", "fullName studentId");

      logger.info(`Absence request created: ${leaveRequest.id}`, {
        leaveRequestId: leaveRequest.id,
        studentId: student.id,
        schoolId: student.schoolId,
      });

      const populatedStudent = populatedRequest
        ? (populatedRequest as any).student
        : null;

      res.status(201).json({
        success: true,
        message: "Absence request submitted successfully",
        data: {
          leaveRequest: {
            id: leaveRequest.id,
            leaveType: leaveRequest.leaveType,
            startDate: leaveRequest.startDate,
            endDate: leaveRequest.endDate,
            reason: leaveRequest.reason,
            status: leaveRequest.status,
            documents: leaveRequest.documents || [],
            student: populatedStudent
              ? {
                  id: populatedStudent.id || populatedStudent._id,
                  fullName: populatedStudent.fullName,
                  studentId: populatedStudent.studentId,
                }
              : {
                  id: student.id,
                  fullName: student.fullName,
                  studentId: student.studentId,
                },
            createdAt: leaveRequest.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create absence request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create absence request",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }
}

export const studentMobileController = new StudentMobileController();
